import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import {
  Project,
  ProjectLifecycle,
  PhaseProgress,
  CreateProjectRequest,
  UpdateProjectRequest,
  ProjectImportOptions,
  ProjectImportPreview,
  ProjectImportResult,
  ProjectImportJob,
} from '../models';
import { ProjectSite } from '../models/site.model';

const ACCENT_COLORS = ['#e05a3f', '#d49b32', '#3d8b72', '#7b6bac'];

const DEFAULT_SITE_NAME_MAP: Record<string, string> = {
  GB: 'Grande-Baie',
  AP60: 'Arvida-AP60',
  ALM: 'Alma',
  LAT: 'Laterrière',
  VAU: 'Vaudreuil',
};

const LIFECYCLE_MAP: Record<string, ProjectLifecycle> = {
  Completed: 'Complété',
  Cancelled: 'Annulé',
};

@Injectable({ providedIn: 'root' })
export class ProjectService {
  private readonly apiUrl = environment.apiUrl;
  private siteNameMap: Record<string, string> = { ...DEFAULT_SITE_NAME_MAP };

  constructor(private http: HttpClient) {}

  getAll(userName: string): Observable<Project[]> {
    return this.http
      .get<any[]>(`${this.apiUrl}/projects?userName=${encodeURIComponent(userName)}`)
      .pipe(map((rows, index) => rows.map((row, i) => this.mapProject(row, i))));
  }

  getById(id: string): Observable<Project> {
    return this.http
      .get<any>(`${this.apiUrl}/projects/${id}`)
      .pipe(map(row => this.mapProject(row, 0)));
  }

  create(payload: CreateProjectRequest): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/projects`, payload);
  }

  update(id: string, payload: UpdateProjectRequest): Observable<any> {
    return this.http.patch<any>(`${this.apiUrl}/projects/${id}`, payload);
  }

  previewImport(file: File, options: ProjectImportOptions): Observable<ProjectImportPreview> {
    return this.http.post<ProjectImportPreview>(
      `${this.apiUrl}/projects/import/preview`,
      this.importFormData(file, options)
    );
  }

  downloadImportTemplate(): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/projects/import/template`, { responseType: 'blob' });
  }

  commitImport(file: File, options: ProjectImportOptions): Observable<ProjectImportResult> {
    return this.http.post<ProjectImportResult>(
      `${this.apiUrl}/projects/import/commit`,
      this.importFormData(file, options)
    );
  }

  startImport(file: File, options: ProjectImportOptions): Observable<ProjectImportJob> {
    return this.http.post<ProjectImportJob>(
      `${this.apiUrl}/projects/import/start`,
      this.importFormData(file, options)
    );
  }

  getImportJob(jobId: string): Observable<ProjectImportJob> {
    return this.http.get<ProjectImportJob>(`${this.apiUrl}/projects/import/jobs/${jobId}`);
  }

  updatePhaseApprovalDate(id: string, phase: string, approvalDate: string | null): Observable<any> {
    return this.http.put<any>(
      `${this.apiUrl}/projects/${id}/phases/${encodeURIComponent(phase)}/approval-date`,
      { approvalDate }
    );
  }

  archive(id: string, archived: boolean): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/projects/${id}/archive`, { archived });
  }

  follow(id: string, userName: string, following: boolean): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/projects/${id}/follow`, {
      userName,
      following,
    });
  }

  changeTemplateVersion(
    id: string,
    templateVersionId: string,
    actor: string
  ): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/projects/${id}/template-version`, {
      templateVersionId,
      actor,
    });
  }

  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/projects/${id}`);
  }

  // ── Mapping helpers ────────────────────────────────────────────────────────

  mapProject(row: any, index: number): Project {
    const lifecycle = this.lifecycleLabel(row.lifecycleStatus);
    const checklist = Array.isArray(row.checklist) ? row.checklist : [];
    const phaseProgress: PhaseProgress[] = Array.isArray(row.phaseProgress)
      ? row.phaseProgress
      : this.buildPhaseProgress(checklist);
    const currentPhase = row.currentPhase || 'Initiation';
    const currentProgress = phaseProgress.find(item => item.phase === currentPhase)?.progress ?? 0;
    const overdue = Number(row.previousIncompleteCount ?? this.countOverdue(checklist, currentPhase));
    const blocked = Number(row.blockedCount ?? checklist.filter(
      (item: any) => item.applicability === 'Applicable' && item.status === 'Blocked'
    ).length);

    return {
      id: row.id,
      rtbs: row.rtbsNumber || undefined,
      portfolioNumber: row.portfolioNumber || undefined,
      title: row.title,
      site: this.siteName(row.siteCode),
      framework: row.frameworkCode,
      complexityCode: Number(row.complexityCode ?? 1),
      manager: row.projectManager || 'Non assigné',
      phase: currentPhase,
      progress: row.phaseItemCount
        ? Math.round((row.phaseCompletedCount / row.phaseItemCount) * 100)
        : currentProgress,
      overdue,
      blocked,
      status:
        lifecycle === 'Actif' && (overdue > 0 || blocked > 0)
          ? 'En problème'
          : lifecycle,
      lifecycle,
      archived: !!row.isArchived,
      followed: !!row.isFollowed,
      hasRtbsNumber: row.hasRtbsNumber !== false,
      isConfidential: !!row.isConfidential,
      confidentialityGroup: row.confidentialityGroup || undefined,
      phaseApprovalDates: row.phaseApprovalDates ?? {},
      templateVersionId: row.templateVersionId,
      phaseProgress,
      accent: ACCENT_COLORS[index % ACCENT_COLORS.length],
    };
  }

  siteName(code: string): string {
    return this.siteNameMap[code] ?? code;
  }

  siteCode(name: string): string {
    const entry = Object.entries(this.siteNameMap).find(([, v]) => v === name);
    return entry ? entry[0] : name;
  }

  setSites(sites: ProjectSite[]): void {
    this.siteNameMap = {
      ...DEFAULT_SITE_NAME_MAP,
      ...Object.fromEntries(sites.map(site => [site.code, site.name])),
    };
  }

  lifecycleLabel(value: string): ProjectLifecycle {
    return LIFECYCLE_MAP[value] ?? 'Actif';
  }

  lifecycleValue(value: ProjectLifecycle): string {
    const map: Record<ProjectLifecycle, string> = {
      Actif: 'Active',
      Complété: 'Completed',
      Annulé: 'Cancelled',
    };
    return map[value];
  }

  private buildPhaseProgress(checklist: any[]): PhaseProgress[] {
    const phases = ['Initiation', 'OoM', 'PFS', 'FS', 'EXE', 'Démarrage', 'Clôture'];
    return phases.map(phase => {
      const items = checklist.filter(item => item.sectionNameSnapshot === phase);
      const applicable = items.filter(item => item.applicability === 'Applicable');
      const completed = applicable.filter(item => item.status === 'Completed').length;
      return {
        phase,
        totalCount: items.length,
        applicableCount: applicable.length,
        completedCount: completed,
        nonApplicableCount: items.length - applicable.length,
        progress: applicable.length ? Math.round(completed * 100 / applicable.length) : 0,
        allNotApplicable: items.length > 0 && applicable.length === 0,
      };
    });
  }

  private countOverdue(checklist: any[], currentPhase: string): number {
    const phases = ['Initiation', 'OoM', 'PFS', 'FS', 'EXE', 'Démarrage', 'Clôture'];
    const currentIndex = phases.indexOf(currentPhase);
    return checklist.filter(item =>
      phases.indexOf(item.sectionNameSnapshot) < currentIndex
      && item.applicability === 'Applicable'
      && item.status !== 'Completed'
    ).length;
  }

  private importFormData(file: File, options: ProjectImportOptions): FormData {
    const form = new FormData();
    form.append('file', file, file.name);
    form.append('defaultFrameworkCode', options.defaultFrameworkCode);
    form.append('defaultComplexityCode', String(options.defaultComplexityCode));
    if (options.confidentialityGroup.trim())
      form.append('confidentialityGroup', options.confidentialityGroup.trim());
    if (options.projectDate)
      form.append('projectDate', options.projectDate);
    return form;
  }
}
